/**
 * Тренажёры-проработки: дерево программ и сценарии.
 *
 * Сценарий — набор экранов (шагов) и переходов между ними. Экран может задавать вопрос (поле ответа),
 * показывать текст для проговаривания (озвучивается по фразам и повторяется до «Стоп») и предлагать
 * кнопки-развилки. Всё описано данными, поэтому одни и те же сценарии работают в вебе, в Telegram
 * и в мобильных обёртках, а переходы проверяются тестами без интерфейса.
 *
 * Тексты взяты из «Мне можно все!» (тренажёры 1–3, актуальная версия) и из прежнего приложения
 * (Страх, Обида, Образ, Качество, Стремление к одиночеству). Слова пользователя подставляются
 * без перефразирования; окончания «думала/думал» берутся из рода обращения в настройках.
 */
import type { Gender } from "./schemas.js";

export type TrainerGender = "f" | "m";
export const trainerGender = (g: Gender | undefined): TrainerGender => (g === "m" ? "m" : "f");

/* ---------- текст со словами пользователя ---------- */

/** Сегмент: статичный текст или слова пользователя (их интерфейс выделяет) */
export type Seg = string | { v: string };
export type Rich = Seg[];

/** {муж|жен} → нужная форма */
export function gx(g: TrainerGender, s: string): string {
  return s.replace(/\{([^|}]*)\|([^}]*)\}/g, (_, m: string, f: string) => (g === "m" ? m : f));
}

/** Шаблон: статичные части проходят через род, подстановки становятся словами пользователя */
function rich(g: TrainerGender) {
  return (strings: TemplateStringsArray, ...values: (string | Rich)[]): Rich => {
    const out: Rich = [];
    strings.forEach((s, i) => {
      if (s) out.push(gx(g, s));
      if (i < values.length) {
        const v = values[i] ?? "…";
        if (Array.isArray(v)) out.push(...v);
        else out.push({ v });
      }
    });
    return out;
  };
}

/** Слова человека в начале предложения — с большой буквы */
const cap = (s: string | undefined) => (s ? s[0]!.toLocaleUpperCase("ru") + s.slice(1) : s);

export const plain = (r: Rich): string => r.map((s) => (typeof s === "string" ? s : s.v)).join("");

const MAX_SENTENCE = 280;

/** Текст для озвучки → отдельные фразы: между ними пауза, длинные режем по запятым */
export function speechSentences(text: string): string[] {
  const parts = (text.match(/[^.!?…]+(?:[.!?…]+[»”"']*|$)/gu) ?? []).map((s) => s.replace(/\s+/g, " ").trim()).filter(Boolean);
  const out: string[] = [];
  for (const p of parts) {
    if (p.length <= MAX_SENTENCE) {
      out.push(p);
      continue;
    }
    let rest = p;
    while (rest.length > MAX_SENTENCE) {
      const cut = Math.max(rest.lastIndexOf(", ", MAX_SENTENCE), rest.lastIndexOf(" ", MAX_SENTENCE));
      const at = cut > 40 ? cut + 1 : MAX_SENTENCE;
      out.push(rest.slice(0, at).trim());
      rest = rest.slice(at).trim();
    }
    if (rest) out.push(rest);
  }
  return out;
}

/* ---------- нормализация ответов ---------- */

/** Пробелы, хвостовая пунктуация; регистр не трогаем — имена остаются с большой буквы */
export function cleanAnswer(s: string): string {
  return s.replace(/\s+/g, " ").trim().replace(/[\s.!?…,;:]+$/u, "");
}

const stripFear = (s: string) => cleanAnswer(s).replace(/^(?:я\s+)?(?:боюсь|страх)\s+/iu, "");
const stripTarget = (s: string) => cleanAnswer(s).replace(/^(?:я\s+)?(?:обижен[аоы]?\s+)?на\s+/iu, "");
const stripQuality = (s: string) => cleanAnswer(s).replace(/^я\s*[—–-]?\s+/iu, "").replace(/^[—–-]\s*/u, "");

/* ---------- модель сценария ---------- */

export type Answers = Record<string, string>;
export interface FlowCtx {
  a: Answers;
  g: TrainerGender;
}
type Txt = (c: FlowCtx) => Rich;

export interface FieldDef {
  key: string;
  /** начало фразы, которую человек продолжает */
  prefix?: Txt;
  placeholder?: string;
  /** подставить в поле, если оно пустое */
  prefill?: (c: FlowCtx) => string;
  /** подсказка под полем — например, напоминание о предыдущем ответе */
  hint?: (c: FlowCtx) => Rich | null;
  normalize?: (s: string) => string;
}

export interface ActionDef {
  label: string;
  primary?: boolean;
  /** нужен непустой ответ в поле экрана */
  requires?: boolean;
  to: string | ((c: FlowCtx) => string);
  /** записать в историю как результат (для «Появилось чувство эйфории» и т. п.) */
  outcome?: string;
  /** очистить ответы перед переходом — новый круг проработки */
  reset?: string[];
  /** выставить флаги */
  set?: Answers;
}

export interface StepDef {
  /** номер экрана: «номер тренажёра.номер экрана» */
  id: string;
  /** название этапа */
  stage: string;
  /** заголовок экрана, если отличается от этапа */
  title?: Txt;
  /** вопрос или пояснение */
  text?: Txt;
  /** пояснение перед текстом для проговаривания (не озвучивается) */
  note?: string;
  /** подсказка «Не могу понять…» — раскрывается по нажатию */
  help?: { label: string; text: string };
  /** текст для проговаривания: показывается и озвучивается */
  script?: Txt;
  field?: FieldDef;
  actions: ActionDef[];
}

export const FINISH = "finish";

export interface TrainerFlow {
  id: string;
  first: string;
  /** из какого ответа берётся название проработки */
  titleKey: string;
  steps: Record<string, StepDef>;
  /** подзаголовок поздравления */
  done?: (g: TrainerGender) => string;
}

export interface TrainerInfo {
  /** номер тренажёра по порядку в дереве: экраны нумеруются «номер.шаг» */
  n: number;
  id: string;
  title: string;
  level: number;
  /** имя иконки в интерфейсе */
  icon: string;
  summary: string;
  ready: boolean;
}

/* ---------- общие тексты ---------- */

const CHEST =
  "Закройте глаза, ваш внутренний взор направлен вперёд. А сейчас переведите его в район своей груди, не глазами, а внутренним ощущением. Сконцентрируйтесь в районе груди и повторяйте громко, уверенно и вслух:";
const IMAGE_HINT = "Не анализируйте образ, просто прочувствуйте его, а затем опишите.";
const REASON_QUESTION = "Что смущает в образе? Какие эмоции у меня вызывает то, что я вижу?";
const REASON_HELP = {
  label: "Не могу понять, что смущает",
  text: "Какие эмоции у меня вызывает то, что я вижу? Как то, что я вижу, связано с моим сознательным запросом? Что в этой картинке неприятно, тревожно или «не моё»?",
};
const EUPHORIA = "✨ Появилось чувство эйфории";
const LIGHTNESS = "{Начал|Начала} испытывать чувство лёгкости";
const MORE = "Ещё что-то смущает";
// с «вы» краткое прилагательное — во множественном числе при любом роде
const DONE_FREE = () => "Вы свободны от этой установки.";

const guilt = (c: FlowCtx, w: string) =>
  rich(c.g)`Можно мне было не хотеть ${w}. Мне так было можно, и мне это было для чего-то нужно. Я {сам|сама} не {хотел|хотела} ${w}. Можно мне было не хотеть. У меня была на то причина. У меня была причина не хотеть ${w}.`;

const belief = (c: FlowCtx, u: Rich | string) =>
  rich(c.g)`Можно мне было решить, что ${u}. Но только я так {думал|думала}, это была только моя установка, только моя. Мне можно было пользоваться этой установкой, но она была только моя.`;

/** «Трансформация образа» (1.5, 3.10): «…этой установкой. Но она была только моя.» */
const imageTransform = (c: FlowCtx, w: string, f: string) =>
  rich(c.g)`Можно мне было думать, что если у меня будет ${w}, то ${f}, но только я так {думал|думала}. Это была только моя установка, только моя. Мне можно было пользоваться этой установкой. Но она была только моя.`;

/** «Трансформация» с «только если» (Страх, Обида, Одиночество) */
const onlyIf = (c: FlowCtx, cond: Rich, f: string) =>
  rich(c.g)`Можно мне было думать, что только если ${cond}${f}, но только я так {думал|думала}. Это была только моя установка, только моя. Мне можно было пользоваться этой установкой, но она была только моя.`;

/* ---------- 1. Выявление установки ---------- */

const beliefDiscovery: TrainerFlow = {
  id: "belief-discovery",
  first: "1.1",
  titleKey: "wish",
  done: DONE_FREE,
  steps: {
    "1.1": {
      id: "1.1",
      stage: "Снимаем чувство вины",
      title: () => ["Ваше желание"],
      text: () => ["Напишите, чего вы хотите. Ваши слова появятся в тексте практики без изменений."],
      field: { key: "wish", placeholder: "Например: больше зарабатывать", normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "1.2" }],
    },
    "1.2": {
      id: "1.2",
      stage: "Снимаем чувство вины",
      title: () => ["Можно мне было не хотеть"],
      script: (c) => guilt(c, c.a.wish!),
      actions: [{ label: "Смотрим образ", primary: true, to: "1.3" }],
    },
    "1.3": {
      id: "1.3",
      stage: "Смотрим образ",
      title: (c) => rich(c.g)`У меня уже есть ${c.a.wish!}`,
      text: () => [`Что я при этом вижу? ${IMAGE_HINT} Представьте, что желание уже исполнилось.`],
      field: { key: "image", placeholder: "Опишите, что видите", normalize: cleanAnswer },
      actions: [{ label: "Продолжить", primary: true, requires: true, to: "1.4" }],
    },
    "1.4": {
      id: "1.4",
      stage: "Находим причину",
      text: () => [REASON_QUESTION],
      help: REASON_HELP,
      field: { key: "feeling", prefix: (c) => rich(c.g)`Если у меня будет ${c.a.wish!}, то`, normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "1.5" }],
    },
    "1.5": {
      id: "1.5",
      stage: "Трансформация",
      script: (c) => imageTransform(c, c.a.wish!, c.a.feeling!),
      actions: [
        { label: "Завершить проработку", primary: true, to: FINISH, outcome: "Установка выявлена" },
        // выявленную установку можно сразу проработать во втором тренажёре
        { label: "Проработать установку", to: "next:belief" },
      ],
    },
  },
};

/* ---------- 2. Установка ---------- */

const beliefWork: TrainerFlow = {
  id: "belief",
  first: "2.1",
  titleKey: "installation",
  done: DONE_FREE,
  steps: {
    "2.1": {
      id: "2.1",
      stage: "Формулируем установку",
      text: () => ["Введите вашу установку в формате «если …, то …»."],
      field: { key: "installation", placeholder: "если у меня будет …, то …", normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "2.2" }],
    },
    "2.2": {
      id: "2.2",
      stage: "Трансформация",
      note: CHEST,
      script: (c) => belief(c, c.a.installation!),
      actions: [{ label: "Смотрим образ", primary: true, to: "2.3" }],
    },
    "2.3": {
      id: "2.3",
      stage: "Смотрим образ",
      // В исходнике: «Произносите “У меня уже есть” и Ваш сознательный запрос» — но запроса в этом
      // тренажёре нет. Если пришли из «Выявления установки», подставляем желание оттуда.
      text: (c) =>
        c.a.wish
          ? rich(c.g)`Произносите: «У меня уже есть ${c.a.wish}». Что вы при этом видите?`
          : ["Произносите: «У меня уже есть» и ваш сознательный запрос. Что вы при этом видите?"],
      field: { key: "installationImage", placeholder: "Опишите, что видите", normalize: cleanAnswer },
      actions: [
        { label: EUPHORIA, primary: true, to: FINISH, outcome: "Появилось чувство эйфории" },
        { label: MORE, to: "2.1", outcome: MORE, reset: ["installation", "installationImage"] },
      ],
    },
  },
};

/* ---------- 3. Ситуация ---------- */

const situation: TrainerFlow = {
  id: "situation",
  first: "3.1",
  titleKey: "request",
  done: DONE_FREE,
  steps: {
    "3.1": {
      id: "3.1",
      stage: "Сознательный запрос",
      text: () => ["Чего вы хотите в этой ситуации? Сформулируйте своими словами."],
      field: { key: "request", placeholder: "Например: спокойные отношения с мамой", normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "3.2" }],
    },
    "3.2": {
      id: "3.2",
      stage: "Снимаем чувство вины",
      title: () => ["Можно мне было не хотеть"],
      script: (c) => [...guilt(c, c.a.request!), " Мне так было можно."],
      actions: [{ label: "Смотрим образ", primary: true, to: "3.3" }],
    },
    "3.3": {
      id: "3.3",
      stage: "Смотрим образ",
      title: (c) => rich(c.g)`У меня уже есть ${c.a.request!}`,
      text: () => [`Что я при этом вижу? ${IMAGE_HINT} Представьте, что желание уже исполнилось.`],
      field: { key: "image", placeholder: "Опишите, что видите", normalize: cleanAnswer },
      // второй круг («Ещё что-то смущает») идёт через вопрос «зачем»
      actions: [{ label: "Продолжить", primary: true, requires: true, to: (c) => (c.a.deep ? "3.8" : "3.4") }],
    },
    "3.4": {
      id: "3.4",
      stage: "Находим причину",
      text: () => [REASON_QUESTION],
      help: REASON_HELP,
      field: { key: "condition", prefix: (c) => rich(c.g)`Если у меня будет ${c.a.request!}, то`, normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "3.5" }],
    },
    "3.5": {
      id: "3.5",
      stage: "Трансформация",
      script: (c) =>
        rich(c.g)`Можно мне было думать, что если у меня будет ${c.a.request!}, то ${c.a.condition!}, но только я так {думал|думала}. Это была только моя установка, только моя. Мне можно было пользоваться этой установкой, но она была только моя.`,
      actions: [{ label: "Смотрим образ", primary: true, to: "3.6" }],
    },
    "3.6": {
      id: "3.6",
      stage: "Смотрим образ",
      text: (c) => rich(c.g)`Произносите: «У меня уже есть ${c.a.request!}». Что вы при этом видите?`,
      field: { key: "review", placeholder: "Опишите, что видите", normalize: cleanAnswer },
      actions: [
        { label: EUPHORIA, primary: true, to: FINISH, outcome: "Появилось чувство эйфории" },
        { label: MORE, to: "3.2", outcome: MORE, set: { deep: "1" }, reset: ["image", "why", "review"] },
      ],
    },
    "3.8": {
      id: "3.8",
      stage: "Ответ на вопрос «зачем»",
      text: (c) => rich(c.g)`Зачем вы не хотите ${c.a.request!}? Что будет, если у вас это будет?`,
      field: { key: "why", normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "3.7" }],
    },
    "3.7": {
      id: "3.7",
      stage: "Трансформация",
      note: CHEST,
      script: (c) => belief(c, rich(c.g)`если у меня будет ${c.a.request!}, то ${c.a.why!}`),
      actions: [
        { label: EUPHORIA, primary: true, to: FINISH, outcome: "Появилось чувство эйфории" },
        { label: "Уточнить установку", to: "3.9", reset: ["refinement"] },
      ],
    },
    "3.9": {
      id: "3.9",
      stage: "Уточняем установку",
      text: () => [REASON_QUESTION],
      help: REASON_HELP,
      field: { key: "refinement", prefix: (c) => rich(c.g)`Если у меня будет ${c.a.request!}, то`, normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "3.10" }],
    },
    "3.10": {
      id: "3.10",
      stage: "Трансформация",
      script: (c) => imageTransform(c, c.a.request!, c.a.refinement!),
      actions: [{ label: "Смотрим образ", primary: true, to: "3.11" }],
    },
    "3.11": {
      id: "3.11",
      stage: "Смотрим образ",
      text: (c) => rich(c.g)`Произносите: «У меня уже есть ${c.a.request!}». Что вы при этом видите?`,
      field: { key: "review", placeholder: "Опишите, что видите", normalize: cleanAnswer },
      actions: [
        { label: EUPHORIA, primary: true, to: FINISH, outcome: "Появилось чувство эйфории" },
        { label: "Задать вопрос «зачем»", to: "3.8", outcome: "Задать вопрос «зачем»", reset: ["why", "review"] },
      ],
    },
  },
};

/** Ответ на «зачем» — опора для продолжения установки, но не готовый текст: «то чтобы …» звучит криво */
const whyHint = (c: FlowCtx): Rich | null => (c.a.why ? rich(c.g)`Вы ответили на «зачем»: «${c.a.why}». Продолжите фразу своими словами.` : null);

/* ---------- 4. Страх ---------- */

const fearCond = (c: FlowCtx) => rich(c.g)`у меня будет страх ${c.a.fear!}, то `;

const fear: TrainerFlow = {
  id: "fear",
  first: "4.1",
  titleKey: "fear",
  done: DONE_FREE,
  steps: {
    "4.1": {
      id: "4.1",
      stage: "Страх",
      title: () => ["Чего вы боитесь?"],
      field: { key: "fear", placeholder: "Например: остаться одной, публичных выступлений", normalize: stripFear },
      actions: [{ label: "Далее", primary: true, requires: true, to: "4.2" }],
    },
    "4.2": {
      id: "4.2",
      stage: "Снимаем чувство вины",
      title: (c) => rich(c.g)`Страх ${c.a.fear!}`,
      script: (c) => rich(c.g)`Можно мне было иметь этот страх, страх — это мой инструмент. Он был мне нужен. Я {сам|сама} его {создавал|создавала}.`,
      actions: [{ label: "Далее", primary: true, to: "4.3" }],
    },
    "4.3": {
      id: "4.3",
      stage: "Выход на установку",
      text: () => ["Можно мне было пользоваться этим инструментом. Зачем?"],
      field: { key: "why", normalize: cleanAnswer },
      actions: [{ label: "Далее", primary: true, requires: true, to: "4.4" }],
    },
    "4.4": {
      id: "4.4",
      stage: "Формируем установку",
      text: () => ["Моя установка:"],
      field: { key: "consequence", prefix: (c) => rich(c.g)`Только если у меня будет страх ${c.a.fear!}, то`, hint: whyHint, normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "4.5" }],
    },
    "4.5": {
      id: "4.5",
      stage: "Трансформация",
      script: (c) => onlyIf(c, fearCond(c), c.a.consequence!),
      actions: [
        { label: LIGHTNESS, primary: true, to: "4.6" },
        { label: "Уточнить установку", to: "4.4" },
      ],
    },
    "4.6": {
      id: "4.6",
      stage: "Образ повторно",
      text: (c) => rich(c.g)`Сформулируйте фразу «${c.a.consequence!}» в настоящем времени — как будто это уже происходит.`,
      field: { key: "present", placeholder: "Например: «меня бросят» → «меня бросают»", normalize: cleanAnswer },
      actions: [{ label: "Далее", primary: true, requires: true, to: "4.7" }],
    },
    "4.7": {
      id: "4.7",
      stage: "Смотрим образ",
      text: (c) => rich(c.g)`${cap(c.a.present)!}. Что я при этом вижу?`,
      field: { key: "image", placeholder: "Опишите, что видите", normalize: cleanAnswer },
      actions: [
        { label: LIGHTNESS, primary: true, to: FINISH, outcome: "Появилось чувство лёгкости" },
        { label: MORE, to: "4.2", outcome: MORE, reset: ["why", "consequence", "present", "image"] },
      ],
    },
  },
};

/* ---------- 5. Обида ---------- */

const PEOPLE_FACING = "Закройте глаза и представьте, что стоите вы и этот человек друг напротив друга. Что между вами?";

const resentment: TrainerFlow = {
  id: "resentment",
  first: "5.1",
  titleKey: "target",
  steps: {
    "5.1": {
      id: "5.1",
      stage: "Обида",
      title: () => ["На кого вы обижены?"],
      field: { key: "target", placeholder: "Например: на маму", normalize: stripTarget },
      actions: [{ label: "Далее", primary: true, requires: true, to: "5.2" }],
    },
    "5.2": {
      id: "5.2",
      stage: "Смотрим образ",
      text: () => [PEOPLE_FACING],
      field: { key: "between", placeholder: "Опишите, что видите", normalize: cleanAnswer },
      actions: [{ label: "Далее", primary: true, requires: true, to: "5.3" }],
    },
    "5.3": {
      id: "5.3",
      stage: "Снимаем чувство вины",
      script: (c) => rich(c.g)`Можно мне было обидеться на ${c.a.target!}, но я {сам|сама} {создал|создала} эту обиду, она была мне для чего-то нужна. Но она была только моя.`,
      actions: [{ label: "Далее", primary: true, to: "5.4" }],
    },
    "5.4": {
      id: "5.4",
      stage: "Выход на установку",
      text: (c) => [gx(c.g, "Я {сам|сама} {создал|создала} обиду, для чего? Продолжите фразу.")],
      field: { key: "forWhat", prefix: (c) => rich(c.g)`Только если у меня будет обида на ${c.a.target!},`, placeholder: "то … / чтобы …", normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "5.5" }],
    },
    "5.5": {
      id: "5.5",
      stage: "Трансформация",
      script: (c) => onlyIf(c, rich(c.g)`у меня будет обида на ${c.a.target!}, `, c.a.forWhat!),
      actions: [
        { label: LIGHTNESS, primary: true, to: "5.6" },
        { label: "Уточнить установку", to: "5.3" },
      ],
    },
    "5.6": {
      id: "5.6",
      stage: "Образ повторно",
      text: () => [PEOPLE_FACING],
      field: { key: "betweenAfter", placeholder: "Опишите, что видите теперь", normalize: cleanAnswer },
      actions: [
        { label: EUPHORIA, primary: true, to: FINISH, outcome: "Появилось чувство эйфории" },
        // в исходнике выхода нет — без него человек застревал, если образ не изменился
        { label: MORE, to: "5.3", outcome: MORE, reset: ["forWhat", "betweenAfter"] },
      ],
    },
  },
};

/* ---------- 6. Образ ---------- */

const image: TrainerFlow = {
  id: "image",
  first: "6.1",
  titleKey: "subject",
  done: () => "Образ изменился.",
  steps: {
    "6.1": {
      id: "6.1",
      stage: "Подсознательный образ",
      text: () => ["Чтобы изменить подсознательный образ, напишите то, что вы хотите изменить: мои деньги, мой мужчина, моя женщина, моё тело, мой бизнес и т. п."],
      field: { key: "subject", placeholder: "Например: мои деньги", normalize: cleanAnswer },
      actions: [{ label: "Далее", primary: true, requires: true, to: "6.2" }],
    },
    "6.2": {
      id: "6.2",
      stage: "Смотрим образ",
      text: (c) => rich(c.g)`${cap(c.a.subject)!}. Что вы при этом видите? Просто описывайте детали образа.`,
      field: { key: "details", placeholder: "Опишите детали", normalize: cleanAnswer },
      actions: [{ label: "Далее", primary: true, requires: true, to: "6.3" }],
    },
    "6.3": {
      id: "6.3",
      stage: "Трансформация",
      // «представление о мои деньги» в исходнике не склоняется — говорим «об образе «…»»
      script: (c) =>
        rich(c.g)`Можно мне было иметь такой образ «${c.a.subject!}», но он был только мой. Это было только моё представление об образе «${c.a.subject!}», можно мне было иметь такое представление, но оно было только моё. Можно мне было пользоваться этим образом «${c.a.subject!}», он был мне для чего-то нужен, но он был только мой.`,
      actions: [{ label: LIGHTNESS, primary: true, to: "6.4" }],
    },
    "6.4": {
      id: "6.4",
      stage: "Образ повторно",
      text: (c) => rich(c.g)`${cap(c.a.subject)!}. Что вы сейчас видите?`,
      field: { key: "after", placeholder: "Опишите, что видите теперь", normalize: cleanAnswer },
      actions: [
        { label: EUPHORIA, primary: true, to: FINISH, outcome: "Появилось чувство эйфории" },
        { label: "Образ ещё не изменился", to: "6.2", outcome: "Образ ещё не изменился", reset: ["details", "after"] },
      ],
    },
  },
};

/* ---------- 7. Качество ---------- */

const iAm = (c: FlowCtx) => rich(c.g)`Я — ${c.a.quality!}.`;
const STRENGTH = "Отрицая это качество, я не {замечал|замечала} в нём какой-то силы, какой? Какая сила есть только в этом качестве? Да, я не {замечал|замечала} её, можно мне было не замечать, но она есть во мне. Что это за сила?";

const quality: TrainerFlow = {
  id: "quality",
  first: "7.1",
  titleKey: "quality",
  done: () => "Это качество снова ваше — вместе с его силой.",
  steps: {
    "7.1": {
      id: "7.1",
      stage: "Что вас раздражает",
      text: () => ["Что вас раздражает в себе или в другом человеке? Обязательно в форме прилагательного."],
      field: { key: "quality", placeholder: "Например: ленивая, навязчивый", normalize: stripQuality },
      actions: [{ label: "Далее", primary: true, requires: true, to: "7.2" }],
    },
    "7.2": {
      id: "7.2",
      stage: "Смотрим образ",
      text: (c) => [...rich(c.g)`Закройте глаза и произнесите: «Я — ${c.a.quality!}». Что вы при этом видите? `, IMAGE_HINT],
      script: iAm,
      field: { key: "image", placeholder: "Опишите, что видите", normalize: cleanAnswer },
      actions: [{ label: "Далее", primary: true, requires: true, to: "7.3" }],
    },
    "7.3": {
      id: "7.3",
      stage: "Снимаем чувство вины",
      script: (c) =>
        rich(c.g)`Можно мне было не принимать это качество в себе, можно мне было отвергать его. Но оно есть во мне. Можно мне было видеть его таким. Но это было только моё представление об этом качестве во мне. Можно мне было иметь такое представление. Но оно было только моё.`,
      actions: [{ label: "Далее", primary: true, to: "7.4" }],
    },
    "7.4": {
      id: "7.4",
      stage: "Находим силу",
      script: (c) => [gx(c.g, STRENGTH)],
      field: { key: "power", placeholder: "Какая это сила?", normalize: cleanAnswer },
      actions: [
        { label: "Далее", primary: true, requires: true, to: "7.5" },
        { label: "Не могу найти силу", to: "7.3" },
      ],
    },
    "7.5": {
      id: "7.5",
      stage: "Смотрим образ повторно",
      text: (c) => rich(c.g)`Закройте глаза и произнесите: «Я — ${c.a.quality!}». Что вы сейчас видите?`,
      script: iAm,
      field: { key: "after", placeholder: "Опишите, что видите теперь", normalize: cleanAnswer },
      actions: [
        { label: EUPHORIA, primary: true, to: FINISH, outcome: "Появилось чувство эйфории" },
        { label: "Неприятные чувства и образ", to: "7.2", outcome: "Неприятные чувства и образ", reset: ["image", "power", "after"] },
      ],
    },
  },
};

/* ---------- Стремление к одиночеству ---------- */

const SOLITUDE_IF = (c: FlowCtx) => [gx(c.g, "я буду отрицать стремление к одиночеству, то ")];

const solitude: TrainerFlow = {
  id: "solitude",
  first: "8.1",
  titleKey: "",
  done: (g) => gx(g, "Вы разрешили себе быть один на один с собой."),
  steps: {
    "8.1": {
      id: "8.1",
      stage: "Смотрим образ",
      text: () => ["Закройте глаза и посмотрите: «Стремление к одиночеству». Что вы при этом видите?"],
      script: () => ["Стремление к одиночеству."],
      field: { key: "image", placeholder: "Опишите, что видите", normalize: cleanAnswer },
      actions: [{ label: "Далее", primary: true, requires: true, to: "8.2" }],
    },
    "8.2": {
      id: "8.2",
      stage: "Снимаем чувство вины",
      script: () => ["Можно мне было отрицать стремление к одиночеству. Мне так было можно, у меня была причина отрицать стремление к одиночеству."],
      actions: [{ label: "Далее", primary: true, to: "8.3" }],
    },
    "8.3": {
      id: "8.3",
      stage: "Находим установку",
      script: () => ["Можно мне было отрицать стремление к одиночеству. Зачем?"],
      field: { key: "why", normalize: cleanAnswer },
      actions: [
        { label: "Я {понял|поняла}, зачем {отрицал|отрицала} стремление к одиночеству", primary: true, requires: true, to: "8.4" },
        { label: "Не могу понять зачем", to: "8.6" },
      ],
    },
    "8.4": {
      id: "8.4",
      stage: "Формируем установку",
      text: () => ["Продолжите фразу:"],
      field: { key: "consequence", prefix: () => ["Только если я буду отрицать стремление к одиночеству, то"], hint: whyHint, normalize: cleanAnswer },
      actions: [{ label: "Сформировать текст", primary: true, requires: true, to: "8.5" }],
    },
    "8.5": {
      id: "8.5",
      stage: "Трансформация",
      script: (c) => onlyIf(c, SOLITUDE_IF(c), c.a.consequence!),
      actions: [
        { label: LIGHTNESS, primary: true, to: "8.7" },
        { label: "Уточнить установку", to: "8.4" },
      ],
    },
    "8.6": {
      id: "8.6",
      stage: "Трансформация",
      script: (c) => [
        gx(c.g, "Можно мне было отрицать стремление к одиночеству, но оно есть во мне. Можно мне было запрещать себе быть {одним|одной}. Теперь мне можно, теперь я разрешаю себе быть один на один с собой."),
      ],
      actions: [{ label: LIGHTNESS, primary: true, to: "8.7" }],
    },
    "8.7": {
      id: "8.7",
      stage: "Образ повторно",
      text: () => ["Стремление к одиночеству. Что вы теперь видите?"],
      field: { key: "after", placeholder: "Опишите, что видите теперь", normalize: cleanAnswer },
      actions: [
        { label: EUPHORIA, primary: true, to: FINISH, outcome: "Появилось чувство эйфории" },
        { label: MORE, to: "8.3", outcome: MORE, reset: ["why", "consequence", "after"] },
      ],
    },
  },
};

/* ---------- дерево программ ---------- */

export const TRAINER_FLOWS: Record<string, TrainerFlow> = Object.fromEntries(
  [beliefDiscovery, beliefWork, situation, fear, resentment, image, quality, solitude].map((f) => [f.id, f]),
);

let seq = 0;
const t = (id: string, title: string, level: number, icon: string, summary: string): TrainerInfo => ({
  n: ++seq,
  id,
  title,
  level,
  icon,
  summary,
  ready: id in TRAINER_FLOWS,
});

/** Порядок — как в прежнем приложении: сначала готовые, затем те, что появятся позже */
export const TRAINERS: TrainerInfo[] = [
  t("belief-discovery", "Выявление установки", 1, "target", "Находим, что мешает желанию сбыться"),
  t("belief", "Установка", 1, "cycle", "Проговариваем установку «если …, то …» и отпускаем её"),
  t("situation", "Ситуация", 2, "bolt", "Сознательный запрос, образ, причина и вопрос «зачем»"),
  t("fear", "Страх", 2, "ghost", "Страх как инструмент: зачем он был нужен"),
  t("resentment", "Обида", 2, "heart-broken", "Что стоит между вами и человеком"),
  t("image", "Образ", 2, "eye", "Меняем подсознательный образ: деньги, тело, отношения"),
  t("quality", "Качество", 2, "thumbs", "Что раздражает — и какая в этом сила"),
  t("solitude", "Стремление к одиночеству", 2, "person", "Разрешаем себе быть один на один с собой"),
  t("desires", "Мои желания", 2, "star", ""),
  t("purpose", "Моё предназначение", 2, "compass", ""),
  t("emotions", "Мои эмоции", 2, "smile", ""),
  t("individuality", "Моя индивидуальность", 2, "fingerprint", ""),
  t("self-love", "Любовь к себе просто так", 2, "heart", ""),
  t("depth", "Глубина", 3, "layers", ""),
  t("connection", "Связь", 3, "link", ""),
  t("self-esteem", "Самооценка", 4, "scale", ""),
  t("norm", "Норма", 4, "ruler", ""),
  t("reverse", "Проработка от обратного", 5, "swap", ""),
  t("division", "Деление", 5, "split", ""),
];

export const trainerInfo = (id: string) => TRAINERS.find((x) => x.id === id);

/**
 * Номер экрана для показа: «номер тренажёра.шаг». Шаг 1 — карточка тренажёра в дереве,
 * дальше экраны по порядку сценария (как они идут по логике, включая ветки).
 * Внутренние id шагов не меняются — сохранённые проработки продолжают открываться.
 */
export function screenNumber(trainerId: string, stepId?: string): string {
  const info = trainerInfo(trainerId);
  const n = info?.n ?? 0;
  if (!stepId) return `${n}.1`;
  const flow = TRAINER_FLOWS[trainerId];
  const idx = flow ? Object.keys(flow.steps).indexOf(stepId) : -1;
  return `${n}.${idx < 0 ? 1 : idx + 2}`;
}

export function trainerLevels(): { level: number; trainers: TrainerInfo[] }[] {
  const levels = [...new Set(TRAINERS.map((x) => x.level))].sort((a, b) => a - b);
  return levels.map((level) => ({ level, trainers: TRAINERS.filter((x) => x.level === level) }));
}

/* ---------- движок ---------- */

export interface ScreenView {
  step: StepDef;
  title: Rich;
  text: Rich | null;
  script: Rich | null;
  prefix: Rich | null;
  hint: Rich | null;
  /** значение поля для показа: ответ или подстановка */
  value: string;
  actions: (ActionDef & { label: string })[];
}

export function renderStep(flow: TrainerFlow, stepId: string, a: Answers, g: TrainerGender): ScreenView {
  const step = flow.steps[stepId] ?? flow.steps[flow.first]!;
  const c: FlowCtx = { a, g };
  const f = step.field;
  return {
    step,
    title: step.title ? step.title(c) : [step.stage],
    text: step.text ? step.text(c) : null,
    script: step.script ? step.script(c) : null,
    prefix: f?.prefix ? f.prefix(c) : null,
    hint: f?.hint ? f.hint(c) : null,
    value: f ? (a[f.key] ?? (f.prefill ? f.prefill(c) : "")) : "",
    actions: step.actions.map((x) => ({ ...x, label: gx(g, x.label) })),
  };
}

export interface ActResult {
  answers: Answers;
  /** следующий экран, FINISH или «next:<тренажёр>» — перейти в другой тренажёр */
  to: string;
  outcome?: string;
  error?: string;
}

/** Нажатие кнопки на экране: проверка ответа, сброс/флаги, следующий экран */
export function act(flow: TrainerFlow, stepId: string, actionIndex: number, input: string | undefined, a: Answers, g: TrainerGender): ActResult {
  const step = flow.steps[stepId]!;
  const action = step.actions[actionIndex]!;
  let answers = { ...a };
  if (step.field) {
    const raw = input ?? answers[step.field.key] ?? step.field.prefill?.({ a: answers, g }) ?? "";
    const value = (step.field.normalize ?? cleanAnswer)(raw);
    if (action.requires && !value) return { answers: a, to: stepId, error: "Напишите несколько слов, чтобы продолжить." };
    if (value) answers[step.field.key] = value;
  }
  for (const k of action.reset ?? []) delete answers[k];
  if (action.set) answers = { ...answers, ...action.set };
  const to = typeof action.to === "function" ? action.to({ a: answers, g }) : action.to;
  return { answers, to, outcome: action.outcome };
}

/** Название проработки: главный ответ или имя тренажёра */
export function workingTitle(flow: TrainerFlow, a: Answers): string {
  const info = trainerInfo(flow.id);
  const v = flow.titleKey ? a[flow.titleKey] : "";
  const base = info?.title ?? "Проработка";
  if (!v) return base;
  const title = flow.id === "resentment" ? `${base}: на ${v}` : `${base}: ${v}`;
  return title.length > 120 ? `${title.slice(0, 117)}…` : title;
}

/** Ответы, которые переносятся в следующий тренажёр («Выявление установки» → «Установка») */
export function carryOver(fromFlow: TrainerFlow, a: Answers): { trainerId: string; answers: Answers } | null {
  if (fromFlow.id === "belief-discovery" && a.wish && a.feeling) {
    return { trainerId: "belief", answers: { wish: a.wish, installation: `если у меня будет ${a.wish}, то ${a.feeling}` } };
  }
  return null;
}

/** Неизменные фразы всех сценариев (без слов человека, в обоих родах) — для прогрева кэша озвучки */
export function trainerFixedSentences(): string[] {
  const MARK = "⁣";
  const a = new Proxy({} as Answers, { get: (_t, k) => (typeof k === "string" && k !== "deep" ? MARK : undefined) });
  const out = new Set<string>();
  for (const flow of Object.values(TRAINER_FLOWS)) {
    for (const step of Object.values(flow.steps)) {
      if (!step.script) continue;
      for (const g of ["f", "m"] as const) {
        for (const s of speechSentences(plain(step.script({ a, g })))) if (!s.includes(MARK)) out.add(s);
      }
    }
  }
  return [...out];
}
